import { useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router"
import { useDispatch, useSelector } from "react-redux"
import "./index.scss"

import Button from "react-bootstrap/Button"
import { Container, Title, Text, Form } from "components/Form"
import { ConfirmDialog, Popup } from "components"
import { auth, loading, message, teamStatus } from "state_management"
import store from "store"
import * as limsCapabilities from "state_management/lims/limsCapabilities"
import useT from "utils/hooks/useT"

export default function LoginScreen({ fromSetPasswordLink = false }) {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const { t } = useT("messages")
  const { t: tc } = useT("common")

  // Login form fields
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [token, setToken] = useState("")
  // Info message visibility and control
  const [showInfoMessage, setShowInfoMessage] = useState(false)
  const [position, setPosition] = useState(null)
  // Token visibility and control
  const [showTokenField, setShowTokenField] = useState(!!parseInt(localStorage.getItem("tokenExpireTime"), 10))
  const [showPopup, setShowPopup] = useState(false)

  // Team picker state (when user belongs to multiple teams)
  const pendingMemberships = useSelector((state) => state.auth.pendingMemberships)
  const [pendingEmail, setPendingEmail] = useState("")
  const [pendingPassword, setPendingPassword] = useState("")

  // SPEC-057 L2 — self-service "forgot password": step 1 asks for the mail (the backend always
  // answers OK — no user enumeration), step 2 takes the emailed 6-digit code + a new password.
  // The same flow serves welcome codes (new employees pick their first password here).
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn)

  // The link in a reset or welcome mail lands on /set-password?email=…&code=…
  //
  // Read ONCE at mount, not from an effect: setting state inside an effect makes React render
  // twice for no reason, and the values are needed for the very first render anyway. The query is
  // stripped straight afterwards, so this is also the only moment it can be read.
  const [linkCredentials] = useState(() => {
    if (!fromSetPasswordLink) {
      return null
    }
    const params = new URLSearchParams(window.location.search)
    const linkedEmail = params.get("email")
    const linkedCode = params.get("code")
    return linkedEmail && linkedCode ? { email: linkedEmail, code: linkedCode } : null
  })
  // While anybody is signed in the link is refused outright (see the render below), so the code is
  // never filled in for them, whoever they are. Logged out it goes straight to the code step,
  // which is the whole point: the only other way in is the button that requests a NEW code, and
  // issuing one kills the code the mail carried.
  const redeemingLink = !!linkCredentials && !isLoggedIn
  const [resetStep, setResetStep] = useState(redeemingLink ? "code" : null) // null | "email" | "code"
  const [resetEmail, setResetEmail] = useState(redeemingLink ? linkCredentials.email : "")
  const [resetCode, setResetCode] = useState(redeemingLink ? linkCredentials.code : "")
  const [resetPassword, setResetPassword] = useState("")

  // Drop the query in BOTH cases, including the refused one: a one-time code must not be left
  // sitting in the browser history either way. Replaced client-side, never reloaded — on
  // http-localhost the token lives in memory and a full reload signs the user out.
  useEffect(() => {
    if (fromSetPasswordLink && location.search) {
      navigate(location.pathname, { replace: true })
    }
  }, [fromSetPasswordLink, location.pathname, location.search, navigate])

  const handleLogin = () => {
    dispatch(auth.login(email, password))
      .then((res) => {
        if (res && res.requires_team_selection) {
          // Capture credentials now before any re-render can affect component state
          setPendingEmail(email)
          setPendingPassword(password)
          return
        }
        handleSuccesfulLogin(res)
      })
      .catch((e) => {
        // If a the user is logged in elsewhere
        if (e.message === "You are logged in somewhere else") {
          setShowPopup(true)
        }
        // If the user needs to verify their device with MFA
        if (e.message === "You need to verify your device with Multi-Factor Authentication") {
          handleGenerateToken()
        }
      })
  }

  const handleSelectTeam = (memberId) => {
    dispatch(auth.selectTeam(pendingEmail, pendingPassword, memberId))
      .then((res) => {
        handleSuccesfulLogin(res)
      })
      .catch((e) => {
        if (e.message === "You are logged in somewhere else") {
          setShowPopup(true)
        } else if (e.message === "You need to verify your device with Multi-Factor Authentication") {
          handleGenerateToken()
        } else {
          // Any other failure — go back to the login form
          dispatch(auth.clearPendingMemberships())
        }
      })
  }

  // #4131 — where a successful login lands. Kept in ONE place because the block below has two
  // copies of this decision (the version-mismatch path and the normal path) and they must not
  // drift. App.jsx has its own copy for the "already logged in, sitting on /login" case.
  const routeAfterLogin = (fetchAllPromise, status, { memberId, isMember }) => {
    const planOrHome = () => {
      if (isMember && status.options.directToMemberPlan) {
        fetchAllPromise.then(() => {
          const memberData = store.getState().members[memberId]
          navigate(memberData?.showOnPlan === true ? "/member/" + memberId : "/")
        })
      } else {
        navigate("/")
      }
    }
    if (!(status.status?.showLims ?? false)) {
      // Not a LIMS team: nothing here can route into LIMS, so behave exactly as before and
      // do not make the login wait on a load it does not need.
      planOrHome()
      return
    }
    // A LIMS team: the quality column starts its day in the LIMS area rather than on the
    // plan. The capability that decides this lives in the MEMBERS slice, which `fetchAll`
    // fills — so the decision has to wait for that load, or it reads an empty slice and
    // silently falls through to the member plan.
    fetchAllPromise.then(() => {
      const stateNow = store.getState()
      const holdsQaRole =
        limsCapabilities.hasQcCapability(stateNow, memberId, limsCapabilities.QA) ||
        limsCapabilities.hasQcCapability(stateNow, memberId, limsCapabilities.QA_MANAGER)
      if (holdsQaRole) {
        navigate("/lims")
        return
      }
      planOrHome()
    })
  }

  const handleSuccesfulLogin = (res) => {
    if (res.role !== "admin" && res.role !== "customer_admin") {
      const isOnlyLibrarian = res.isOnlyLibrarian
      const isMember = res.role === "member"
      const memberId = res.id
      dispatch(teamStatus.fetchCurrent()).then((res) => {
        const onlyLibrarian = !res.status.showAnalyses && !res.status.showProjects && res.status.showLibrarian
        // This checks if FE in browser is behind FE on server and reloads
        // Page if it is. Otherwise we fetch all as usual
        const oldVersion = JSON.parse(window.localStorage.getItem("plannertechVersion"))
        if (oldVersion !== res.status.plannertechVersion) {
          var localStorageEnabled = true
          try {
            window.localStorage.setItem("plannertechVersion", JSON.stringify(res.status.plannertechVersion))
          } catch {
            localStorageEnabled = false
          }
          if (localStorageEnabled) {
            document.location.reload()
          } else {
            const fetchAllPromise = dispatch(loading.fetchAll())
            dispatch(message.success(t("adhoc.welcomeBack")))
            if (isOnlyLibrarian || onlyLibrarian) {
              navigate("/librarian")
            } else {
              routeAfterLogin(fetchAllPromise, res, { memberId, isMember })
            }
          }
        } else {
          const fetchAllPromise = dispatch(loading.fetchAll())
          dispatch(message.success(t("adhoc.welcomeBack")))
          if (isOnlyLibrarian || onlyLibrarian) {
            navigate("/librarian");
          } else {
            routeAfterLogin(fetchAllPromise, res, { memberId, isMember })
          }
        }
      })
    } else if (res.role === "admin") {
      dispatch(message.success(t("adhoc.welcomeBack")))
      navigate("/locations")
    } else {
      dispatch(message.success(t("adhoc.welcomeBack")))
      navigate("/customers")
    }
  }

  const handleGenerateToken = () => {
    dispatch(auth.getMfaToken(email, password))
      .then(() => {
        setShowTokenField(true)
        setShowInfoMessage(true)
      })
      .catch((e) => {
        if ( e.message === "Too many Authentication code requests today. Your account have been blocked until tomorrow" ) {
          resetView()
        }
      })
  }

  const handleTokenVerification = () => {
    // Verify the token logic
    dispatch(auth.verifyMfaToken(email, password, token))
      .then((res) => {
        setShowTokenField(false)
        if (res && res.requires_team_selection) {
          setPendingEmail(email)
          setPendingPassword(password)
          return
        }
        handleSuccesfulLogin(res)
      })
      .catch((e) => {
        // If a the user is logged in elsewhere
        if (e.message === "You are logged in somewhere else") {
          setShowPopup(true)
        } else if (
          e.message === "Too many wrong attempts" ||
          e.message === "Authentication code has expired" ||
          e.message === "Too many Authentication code requests today. Your account have been blocked until tomorrow"
        ) {
          resetView()
        }
      })
  }

  function handleSubmit() {
    console.log("logging in...")
    if (showTokenField) {
      handleTokenVerification()
    } else {
      handleLogin()
    }
  }

  const resetView = () => {
    // reset states
    setShowTokenField(false)
    setShowPopup(false)
    setToken("")
  }

  const closeResetFlow = () => {
    setResetStep(null)
    setResetCode("")
    setResetPassword("")
  }

  // Same policy as the member form; the backend's model validator is the law.
  const passwordRequirements = [
    { name: tc("atLeast8Characters"), pattern: /^.{8,}$/ },
    { name: tc("atLeast1LowercaseLetter"), pattern: /[a-z]/ },
    { name: tc("atLeast1UppercaseLetter"), pattern: /[A-Z]/ },
    { name: tc("atLeast1Number"), pattern: /\d/ },
  ]

  const handleRequestResetCode = () => {
    dispatch(auth.requestPasswordReset(resetEmail))
      .then(() => setResetStep("code"))
      // A failure here is infrastructure (rate limit / server) — the toast explains, stay put.
      .catch(() => {})
  }

  const handleResetSubmit = () => {
    if (resetStep === "email") {
      handleRequestResetCode()
      return
    }
    const missing = passwordRequirements.filter((r) => !r.pattern.test(resetPassword)).map((r) => r.name)
    if (missing.length > 0) {
      dispatch(message.warning(tc("missingPasswordRequirements", { requirements: missing.join(", ") })))
      return
    }
    dispatch(auth.setPassword(resetEmail, resetCode, resetPassword))
      .then(() => {
        // Back to the login form with the mail pre-filled; the success toast says the rest.
        setEmail(resetEmail)
        closeResetFlow()
      })
      // Wrong/expired code, rate limit — the message layer toasts it; stay in the flow.
      .catch(() => {})
  }

  // A set-password link is REFUSED outright while anybody is signed in: no code read, nothing
  // filled in, no way through. The rule ignores WHO is signed in — the account the link belongs to
  // and a different one both land here — because every exception is another path to reason about
  // and another way to leave someone unsure whose password they are about to change.
  //
  // It carries a link back into the app rather than only a message: this route renders outside the
  // app's normal chrome, so without one the page would have no navigation at all, and a dead end
  // is precisely the failure this whole change exists to remove.
  if (fromSetPasswordLink && isLoggedIn) {
    return (
      <div className="LoginScreen-main-container">
        <div className="LoginScreen-content">
          <h1>{tc("plannertech")}</h1>
          <h4>{tc("setPasswordLogOutFirstTitle")}</h4>
          <p className="login-reset-info">{tc("setPasswordLogOutFirst")}</p>
          <Button variant="link" className="login-back-button" onClick={() => navigate("/")}>
            {tc("backToPlannertech")}
          </Button>
        </div>
      </div>
    )
  }

  // Forgot-password flow (SPEC-057 L2) — replaces the login form until done/cancelled.
  if (resetStep) {
    return (
      <div className="LoginScreen-main-container">
        <Form className="LoginScreen-content" onSubmit={handleResetSubmit}>
          <h1>{tc("plannertech")}</h1>
          <h4>{tc("forgotPasswordTitle")}</h4>
          <Button variant="link" className="login-back-button" onClick={closeResetFlow}>
            ← {tc("back")}
          </Button>
          <Container>
            <Title>{tc("email")}</Title>
            <Text required type="email" value={resetEmail} disabled={resetStep === "code"} onChange={setResetEmail} />
          </Container>
          {resetStep === "code" && (
            <>
              <p className="login-reset-info">{tc("passwordResetCodeSentInfo")}</p>
              <Container>
                <Text
                  type="text"
                  required
                  placeholder={tc("passwordResetCode")}
                  value={resetCode}
                  onChange={setResetCode}
                />
              </Container>
              <Container>
                <Title>{tc("newPassword")}</Title>
                <Text required type="password" value={resetPassword} onChange={setResetPassword} />
              </Container>
            </>
          )}
          <Button className="login-button" type="submit">
            {resetStep === "email" ? tc("sendResetCode") : tc("setNewPassword")}
          </Button>
          {resetStep === "code" && (
            <Button variant="link" type="button" className="login-resend-button" onClick={handleRequestResetCode}>
              {tc("resendResetCode")}
            </Button>
          )}
        </Form>
      </div>
    )
  }

  // Team picker screen shown when user belongs to multiple teams
  if (pendingMemberships) {
    return (
      <div className="LoginScreen-main-container">
        <div className="LoginScreen-content">
          <h1>{tc("plannertech")}</h1>
          <h4>{tc("selectTeam")}</h4>
          <Button
            variant="link"
            className="login-back-button"
            onClick={() => dispatch(auth.clearPendingMemberships())}
          >
            ← {tc("back")}
          </Button>
          <div className="team-selector-list">
            {pendingMemberships.map((membership) => (
              <button
                key={membership.id}
                className="team-selector-item"
                onClick={() => handleSelectTeam(membership.id)}
              >
                {membership.initials && (
                  <span className="team-selector-item__avatar">{membership.initials}</span>
                )}
                <span className="team-selector-item__text">
                  <span className="team-selector-item__name">
                    {membership.team_name ?? (membership.role === "admin" ? tc("admin") : tc("customerAdmin"))}
                  </span>
                  {membership.location_name && (
                    <span className="team-selector-item__location">{membership.location_name}</span>
                  )}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="LoginScreen-main-container">
      <Form className="LoginScreen-content" onSubmit={handleSubmit}>
        {showPopup && (
          <ConfirmDialog
            onCancel={() => setShowPopup(false)}
            onConfirm={() => {
              setShowPopup(false)
              console.log("logging in...")
              dispatch(auth.logoutOtherDevice(email, password))
                .then((res) => {
                  if (res && res.requires_team_selection) {
                    setPendingEmail(email)
                    setPendingPassword(password)
                    return
                  }
                  handleSuccesfulLogin(res)
                })
                .catch((e) => {
                  if (e.message === "You need to verify your device with Multi-Factor Authentication") {
                    setShowTokenField(true)
                  }
                })
            }}
          >
            <h4>{tc("userAlreadyLoggedInTakeOver")}</h4>
          </ConfirmDialog>
        )}
        <h1>{tc("plannertech")}</h1>
        <Container>
          <Title>{tc("email")}</Title>
          <Text
            required
            type="email"
            value={email}
            onChange={(str) => {
              setEmail(str)
              if (showTokenField) {
                resetView()
              }
            }}
          />
        </Container>
        <Container>
          <Title>{tc("password")}</Title>
          <Text
            required
            type="password"
            value={password}
            onChange={(str) => {
              setPassword(str)
              if (showTokenField) {
                resetView()
              }
            }}
          />
        </Container>
        {showTokenField && (
          <>
            <Container>
              <Text type="text" required placeholder={tc("authenticationCode")} value={token} onChange={setToken} />
            </Container>
            {showInfoMessage && (
              <Popup
                onCancel={() => setShowInfoMessage(false)}
                singleMessage={
                  <p>{tc("authenticationCodeSentInfo")}</p>
                }
                position={position}
              />
            )}
          </>
        )}
        <Button
          className="login-button"
          type="submit"
          onClick={(event) => {
            if (!showTokenField) {
              const buttonRect = event.target.getBoundingClientRect()
              setPosition({ top: buttonRect.top })
            }
          }}
        >
          {tc("login")}
        </Button>
        <Button
          variant="link"
          type="button"
          className="login-forgot-button"
          onClick={() => {
            setResetEmail(email)
            setResetStep("email")
          }}
        >
          {tc("forgotPassword")}
        </Button>
      </Form>
    </div>
  )
}
